import { CustomValidationRenderer } from '@rightship-tech/common-ui/client-validation-system/custom-validation-renderer';
import { AttachmentFile } from "@rightship-tech/common-ui/models/attachments.models";
import { autoinject, observable, computedFrom } from "aurelia-framework";
import { ValidationController, ValidationControllerFactory, ValidationRules } from 'aurelia-validation';
import { CurrencyModel, GetInspectionInvoiceSubmissionInfoResponse } from '../../../models/rightship-inspections.models';
import { RightshipInspectionService } from '../../../services/rightship-inspection.service';
import { Router } from 'aurelia-router';
import { RsInspectionRouteConstants } from '../../../constants/route.constants';
import { LookupClientService } from '@rightship-tech/common-ui/services/lookups/lookup-client.service';
import { LookupValueRequest } from '@rightship-tech/common-ui/models/lookup.models';
import { LookupValueQuery } from '@rightship-tech/common-ui/constants/lookup.constants';
import { NotificationClientService, NotificationType } from '@rightship-tech/common-ui/services/basic/notification-client.service';

@autoinject
export class Invoice {
    public exceededThresholdPercentage = 15;
    public currenciesList: CurrencyModel[] = [];
    public invoiceDetailModel: GetInspectionInvoiceSubmissionInfoResponse;
    public currency: CurrencyModel;
    public costOverrunThreshold: number;
    public inspectionAmount: number;
    public travelExpensesAmount: number;
    public reason: string;
    public invoiceNumber: string;
    public attachments: AttachmentFile[] = [];
    public title: string = "Submit invoice for RightShip inspection order #";
    
    @observable
    public doubleInpector: boolean = false;

    private _encryptedData: string;
    private _validator: ValidationController;
    private _rsInspectionId: number;
    accept: string = "application/pdf";
    extensions: string = ".PDF";
    amountFilesAllowed: number = 1;

    constructor(validationControllerFactory: ValidationControllerFactory, private rightshipInspectionService: RightshipInspectionService,
        private router: Router, private lookupService: LookupClientService, private notificationService: NotificationClientService
    ) {
        this._validator = validationControllerFactory.createForCurrentScope();
        this._validator.addRenderer(new CustomValidationRenderer());
    }

    async activate(params: { encryptedData: string }) {
        this._encryptedData = decodeURIComponent(params.encryptedData);
        this.invoiceDetailModel = await this.rightshipInspectionService.getInspectionInvoiceSubmissionInfo(this._encryptedData);
        this.invoiceDetailModel.rightShipInspectionId = this.invoiceDetailModel.inspectionId;
        this._rsInspectionId = this.invoiceDetailModel.inspectionId;
        if (this.invoiceDetailModel.hasSubmitted) {
            this.router.navigateToRoute(RsInspectionRouteConstants.SubmittedInvoice, { 
                vesselName: this.invoiceDetailModel.vesselDetails.vesselName,
                vesselImo: this.invoiceDetailModel.vesselDetails.vesselImo, 
                portName: this.invoiceDetailModel.portAndOperationalDetails.port,
                country: this.invoiceDetailModel.portAndOperationalDetails.country,
                invoiceNumber: this.invoiceDetailModel.invoiceNumber,
                requestedInspectionDate: this.invoiceDetailModel.portAndOperationalDetails.requestedInspectionDate,
                rsInspectionId: this._rsInspectionId,
                encryptedData: encodeURIComponent(this._encryptedData)
            });
            return;
        }
    }

    async attached() {
        this.getInvoiceDetails();
        this.setUpValidation();
        this.lookupService.getLookup(new LookupValueRequest(LookupValueQuery.CurrencyConversionRates)).then((response) => {
            this.currenciesList = response.activeValues.map(x=> { return { id: x.id, name: x.name, rate: x.additionalData['rate']} });
        });
    }

    getInvoiceDetails() {
        this.invoiceNumber = this.invoiceDetailModel.invoiceNumber;
        this.title += this.invoiceNumber;
        this.currency = {
            id: null,
            name: null,
            rate: null
        }
    }

    setUpValidation() {
        this.getRules();
    }

    getRules(){
        const threshold = (100 + this.exceededThresholdPercentage) / 100;
        const thresholdOverrun = this.invoiceDetailModel.estimatedCost * threshold;
        ValidationRules
            .ensure("reason")
            .satisfies(x => {
                if (this.totalAmount * this.currencyConversionRate > thresholdOverrun) {
                    return x && x !== "";
                } else {
                    return true;
                }
            })
            .withMessage("Please enter justification for exceeding the estimated amount")
            .ensure("attachments")
            .satisfies(x => x && x.length > 0)
            .withMessage("Please choose one document")
            .satisfies(x => x.length > 0 && x[0].type === "application/pdf")
            .withMessage("Only .PDF files are accepted")
            .on(this);

        ValidationRules
            .ensure<CurrencyModel, any>(x => x.name)
            .required()
            .withMessage("Please choose currency")
            .on(this.currency);
    }

    getTotalAmountValidate() {
        if(this.totalAmount > 0) {
            return true;
        }
        this.notificationService.show("Validation error", "Please enter inspection amount or travel expenses amount", NotificationType.Error);
        return false;
    }

    @computedFrom("currency", "currency.name")
    get currencyConversionRate(): number {
        const rate = this.currenciesList.find(x => x.name === this.currency.name)?.rate;
        return rate ? rate : 1;
    }
    
    @computedFrom("inspectionAmount", "travelExpensesAmount")
    get totalAmount(): number {
        const inspectionCost = this.inspectionAmount ?? 0;
        const travelCost = this.travelExpensesAmount ?? 0;
        return inspectionCost + travelCost;
    }

    @computedFrom("totalAmount")
    get formattedTotalAmount(): string {
        return this.totalAmount.toLocaleString();
    }

    @computedFrom("inspectionAmount", "travelExpensesAmount", "currency.id", "currency.name")
    get shouldDisplayCostOverrun(): boolean {
        const threshold = (100 + this.exceededThresholdPercentage) / 100;
        const thresholdOverrun = this.invoiceDetailModel.estimatedCost * threshold;
        return this.totalAmount * this.currencyConversionRate > thresholdOverrun;
    }

    async save() {
        const validator = await this._validator.validate();
        if (validator.valid && this.getTotalAmountValidate()) {
            const response = await this.rightshipInspectionService.submitRightShipInspectionInvoice({
                invoiceCurrency: this.currency.name,
                inspectionAmount: this.inspectionAmount,
                travelExpenseAmount: this.travelExpensesAmount,
                costOverrunReason: this.reason,
                attachments: this.attachments,
                invoiceNumber: this.invoiceNumber
            }, encodeURIComponent(this._encryptedData));

            this.router.navigateToRoute(RsInspectionRouteConstants.SubmittedInvoice, { 
                vesselName: response.vesselName,
                vesselImo: response.vesselImo, 
                portName: response.portName,
                country: response.portCountry,
                invoiceNumber: this.invoiceNumber,
                requestedInspectionDate: response.inspectionDate,
                rsInspectionId: this._rsInspectionId,
                encryptedData: encodeURIComponent(this._encryptedData)
         });
        }
    }

    //Remove file
    public removeFile(index: number): void {
        if (index > -1 && this.attachments) {
            this.attachments.splice(index, 1);
        }
    }

}